/**
 * @element qds-rich-text-editor
 *
 * Form-associated rich text input for narrative HTML. It provides a compact,
 * curated Quill toolbar while retaining the standard QDS form-control API.
 * Use `qds-inline-rich-text` only for the content-authoring canvas.
 *
 * @attr {string} value - Current HTML value.
 * @attr {string} name - Submitted form key.
 * @attr {string} label - Visible field label.
 * @attr {string} hint - Helper text below the editor.
 * @attr {string} error-text - Author-provided validation error.
 * @attr {string} placeholder - Shown while the editor is empty.
 * @attr {'basic'|'narrative'|'full'} toolbar - Toolbar preset. Default `narrative`.
 * @attr {boolean} disabled - Disables editing and form submission.
 * @attr {boolean} readonly - Makes the content non-editable.
 * @attr {boolean} required - Requires text content.
 * @attr {boolean} invalid - Author-forced invalid state.
 * @attr {'blur'|'input'|'submit'} validate-on - Validation timing. Default `blur`.
 *
 * @fires input - Fired for user edits; detail `{ component, value }`.
 * @fires change - Fired when an edited value is committed on blur.
 * @fires qds-validity-change - Fired when validity changes.
 */

import Quill from 'quill';
import QdsColorPicker from '../color-picker/qds-color-picker.js';
import QdsFontPicker from '../font-picker/qds-font-picker.js';
import { adoptPrimeIconsStyles, getPrimeIconsStyleImport, loadBaseCSS } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons } from '../shared/icon-utils.js';
import {
  attachInternalsSafe,
  ValidityTracker,
  dispatchValidityChange,
  generateControlId,
  isAttrTruthy,
  reflectBoolean,
  parseValidateOn,
  upgradeProperty,
} from '../shared/form-control.js';
import type { QdsTypographyStyle, QdsValidateOn, QdsValidity } from '../../types/index.js';

const CURATED_FORMATS = [
  'bold', 'italic', 'underline', 'strike', 'link', 'blockquote', 'list',
];
const BLOCK_FORMATS = new Set(['align', 'blockquote', 'code-block', 'header', 'indent', 'list']);
const QDS_FONT_FAMILIES = ['ibm-plex-sans', 'eb-garamond', 'roboto', 'open-sans'];

// Use the font-picker's named QDS families and pixel sizes directly in the
// serialized Quill document instead of reducing them to generic serif/sans.
const QdsFontFormat = Quill.import('formats/font');
QdsFontFormat.whitelist = QDS_FONT_FAMILIES;
const QdsSizeFormat = Quill.import('attributors/style/size');
QdsSizeFormat.whitelist = undefined;
Quill.register(QdsFontFormat, true);
Quill.register(QdsSizeFormat, true);
type ToolbarPreset = 'basic' | 'narrative' | 'full';
type QuillSelectionRange = { index: number; length: number };
type QuillIndexableBlot = { index(node: Node, offset: number): number };
type QuillSelectionState = {
  lastRange: QuillSelectionRange | null;
  savedRange: QuillSelectionRange;
};
const LIGHT_DOM_STYLES_ID = 'qds-rich-text-editor-light-dom-styles';

function ensureLightDomEditorStyles(): void {
  if (document.getElementById(LIGHT_DOM_STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = LIGHT_DOM_STYLES_ID;
  style.textContent = `
    qds-rich-text-editor > .qds-rich-text-editor__editor { --qds-rich-text-editor-inline-padding: 14px; min-height: 120px; padding: 12px var(--qds-rich-text-editor-inline-padding); }
    qds-rich-text-editor > .qds-rich-text-editor__editor.ql-container { box-sizing: border-box; position: relative; border: 0 !important; outline: 0; box-shadow: none; background: transparent; font: inherit; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor { box-sizing: border-box; min-height: 96px; padding: 0; outline: 0; overflow-y: auto; font: inherit; line-height: 1.6; text-align: left; white-space: pre-wrap; overflow-wrap: break-word; caret-color: currentColor; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor > * { cursor: text; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor.ql-blank::before { content: attr(data-placeholder); position: absolute; left: var(--qds-rich-text-editor-inline-padding); right: var(--qds-rich-text-editor-inline-padding); pointer-events: none; color: var(--qds-rich-text-editor-placeholder-color, #80868b); font-style: normal; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor p, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor ul, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor ol, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor blockquote { margin: 0 0 .8em; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor ol { padding-left: 1.5em; counter-reset: qds-list; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor li { position: relative; padding-left: 1.5em; list-style: none; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor li > .ql-ui::before { display: inline-block; width: 1.2em; margin-left: -1.5em; margin-right: .3em; text-align: right; white-space: nowrap; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor li[data-list="bullet"] > .ql-ui::before { content: '•'; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor li[data-list="ordered"] { counter-increment: qds-list; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor li[data-list="ordered"] > .ql-ui::before { content: counter(qds-list, decimal) '. '; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor p:last-child, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor ul:last-child, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor ol:last-child, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor blockquote:last-child { margin-bottom: 0; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor blockquote { margin-left: 0; padding-left: 12px; border-left: 3px solid var(--theme-border-strong, #c5c9ce); color: var(--theme-text-secondary, #5f6368); }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor h1, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor h2, qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor h3 { margin: 0 0 .5em; line-height: 1.25; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor h1 { font-size: 1.7em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor h2 { font-size: 1.4em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor h3 { font-size: 1.15em; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-editor .ql-code-block-container { margin: 0 0 .8em; padding: 12px; border-radius: 4px; background: var(--theme-background-secondary, #f8f8f8); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-align-center { text-align: center; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-align-right { text-align: right; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-align-justify { text-align: justify; }
    qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-1 { padding-left: 3em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-2 { padding-left: 6em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-3 { padding-left: 9em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-4 { padding-left: 12em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-5 { padding-left: 15em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-6 { padding-left: 18em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-7 { padding-left: 21em; } qds-rich-text-editor > .qds-rich-text-editor__editor .ql-indent-8 { padding-left: 24em; }
  `;
  document.head.appendChild(style);
}

function normalizeHtml(quill: Quill): string {
  return quill.getText().trim() ? quill.root.innerHTML.trim() : '';
}

function parseToolbar(value: string | null): ToolbarPreset {
  // `minimal` was used during the component's initial preview. Keep it as an
  // alias so those pages retain their compact emphasis-only toolbar.
  if (value === 'basic' || value === 'minimal') return 'basic';
  if (value === 'full') return 'full';
  return 'narrative';
}

/** A first-class QDS form control for formatted narrative text. */
export class QdsRichTextEditor extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;

  private _internals?: ElementInternals;
  private _tracker: ValidityTracker;
  private _id = generateControlId('rich-text-editor');
  private _value = '';
  private _initialValue = '';
  private _lastCommittedValue = '';
  private _quill: Quill | null = null;
  private _syncingValue = false;
  private _errorVisible = false;
  private _lastValid = true;
  private _lastMessage = '';
  private _customValidityMessage = '';
  private _lastSelectionRange: QuillSelectionRange | null = null;
  private _pointerActivatedToolbarControl: HTMLElement | null = null;
  private _lastToolbarActivation: { control: HTMLElement; timestamp: number } | null = null;
  private _editorMount: HTMLElement | null = null;

  static get observedAttributes(): string[] {
    return ['value', 'name', 'label', 'hint', 'error-text', 'placeholder', 'disabled', 'readonly', 'required', 'invalid', 'validate-on'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._internals = attachInternalsSafe(this);
    if (this._internals && typeof this._internals.setFormValue !== 'function') {
      this._internals = undefined;
    }
    this._tracker = new ValidityTracker(this._internals);
    loadBaseCSS();
    ensureLightDomEditorStyles();
  }

  get value(): string { return this._value; }
  set value(value: string) {
    this._value = String(value ?? '');
    this.setAttribute('value', this._value);
    this._syncValueToEditor();
    this._commitFormValue();
    this._updateValidity(false);
  }

  get name(): string { return this.getAttribute('name') || ''; }
  set name(value: string) { this.setAttribute('name', value); }

  get disabled(): boolean { return isAttrTruthy(this.getAttribute('disabled')); }
  set disabled(value: boolean) { reflectBoolean(this, 'disabled', Boolean(value)); }

  get readonly(): boolean { return isAttrTruthy(this.getAttribute('readonly')); }
  set readonly(value: boolean) { reflectBoolean(this, 'readonly', Boolean(value)); }

  get required(): boolean { return isAttrTruthy(this.getAttribute('required')); }
  set required(value: boolean) { reflectBoolean(this, 'required', Boolean(value)); }

  get validity(): QdsValidity { return this._tracker.validity; }
  get form(): HTMLFormElement | null { return this._internals?.form || null; }

  connectedCallback(): void {
    if (this.shadowRoot) void adoptPrimeIconsStyles(this.shadowRoot);
    upgradeProperty(this, 'value');
    this._initialValue = this.getAttribute('value') || this._value;
    this._value = this._initialValue;
    this._lastCommittedValue = this._value;
    this.render();
    this._ensureQuill();
    // Parser-set attributes can trigger one final toolbar render immediately
    // after connection. Ensure that final mount, rather than a stale mount,
    // receives the editor instance.
    queueMicrotask(() => this._ensureQuill());
    this.shadowRoot?.addEventListener('pointerup', this._handleSelectionChange as EventListener);
    this._syncValueToEditor();
    this._syncDom();
    this._commitFormValue();
    this._updateValidity(false);
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('pointerup', this._handleSelectionChange as EventListener);
    this._quill?.root.removeEventListener('keyup', this._handleSelectionChange);
    this._quill = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (name === 'value') {
      this._value = newValue || '';
      this._syncValueToEditor();
      this._commitFormValue();
    }
    if (this.isConnected) {
      this._syncDom();
      this._updateValidity(this._errorVisible);
    }
  }

  formResetCallback(): void { this.value = this._initialValue; this._lastCommittedValue = this._value; this._errorVisible = false; }
  formDisabledCallback(disabled: boolean): void { this.disabled = disabled; }
  checkValidity(): boolean { this._updateValidity(false); return this._tracker.checkValidity(); }
  reportValidity(): boolean { this._errorVisible = true; this._updateValidity(true); return this._tracker.reportValidity(); }
  setCustomValidity(message: string): void {
    this._customValidityMessage = String(message || '');
    this._errorVisible = Boolean(this._customValidityMessage);
    this._updateValidity(this._errorVisible);
  }

  private _ensureQuill(): void {
    if (this._quill || !this.shadowRoot) return;
    const mount = this._getEditorMount();
    const toolbar = this.shadowRoot.querySelector<HTMLElement>('.qds-rich-text-editor__toolbar');
    if (!mount || !toolbar) return;
    const toolbarPreset = parseToolbar(this.getAttribute('toolbar'));
    this._quill = new Quill(mount, {
      bounds: mount,
      modules: { toolbar: false },
      placeholder: this.getAttribute('placeholder') || '',
      readOnly: this.disabled || this.readonly,
      theme: null as unknown as string | undefined,
      // Quill 2 resolves the full document formats through its default
      // registry. Its restricted-registry path is retained for the compact
      // presets only; it cannot resolve the complete document-format set.
      ...(toolbarPreset === 'full' ? {} : { formats: CURATED_FORMATS }),
    });
    toolbar.addEventListener('pointerdown', this._handleToolbarPointerDown);
    toolbar.addEventListener('pointerup', this._handleToolbarPointerUp);
    toolbar.addEventListener('pointerdown', this._handleColorPickerMouseDown);
    toolbar.addEventListener('pointerdown', this._handleFontPickerMouseDown);
    toolbar.addEventListener('click', this._handleToolbarClick);
    toolbar.addEventListener('input', this._handleToolbarChange);
    toolbar.addEventListener('change', this._handleToolbarChange);
    toolbar.addEventListener('value-change', this._handleColorValueChange as EventListener);
    toolbar.addEventListener('value-change', this._handleFontValueChange as EventListener);
    this._quill.root.setAttribute('role', 'textbox');
    this._quill.root.setAttribute('aria-multiline', 'true');
    this._quill.root.addEventListener('keyup', this._handleSelectionChange);
    this._quill.on('text-change', (_delta, _old, source) => {
      if (this._syncingValue || source !== 'user') return;
      this._value = normalizeHtml(this._quill!);
      this._commitFormValue();
      if (this._validateOn() === 'input' || this._errorVisible) this._updateValidity(true);
      this.dispatchEvent(new CustomEvent('input', { detail: { component: this, value: this._value }, bubbles: true, composed: true }));
    });
    this._quill.root.addEventListener('blur', this._handleBlur);
  }

  private _handleSelectionChange = (): void => {
    if (!this.disabled && !this.readonly) this._recordShadowSelectionRange();
  };

  private _handleToolbarPointerDown = (event: PointerEvent): void => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('button[data-format], button[data-action]');
    // The color and font pickers manage their own trigger so their dialogs can
    // open without stealing focus. Every other toolbar button must preserve the
    // selected text before a touch click would move focus away from the editor.
    if (!button || button.closest('qds-color-picker, qds-font-picker')) return;
    this._recordShadowSelectionRange();
    event.preventDefault();
  };

  private _handleToolbarPointerUp = (event: PointerEvent): void => {
    const control = (event.target as HTMLElement | null)?.closest<HTMLElement>('button[data-format], button[data-action="clear-format"]');
    if (!control || control.closest('qds-color-picker, qds-font-picker')) return;

    this._activateToolbarControlFromInteraction(control);
  };

  private _activateToolbarControlFromInteraction(control: HTMLElement): void {
    const timestamp = performance.now();
    if (this._lastToolbarActivation?.control === control
      && timestamp - this._lastToolbarActivation.timestamp < 250) return;
    this._lastToolbarActivation = { control, timestamp };

    // iOS can suppress the synthetic click after a prevented pointerdown.
    // Apply the action at pointerup/touchend so it does not depend on that click.
    this._pointerActivatedToolbarControl = control;
    window.setTimeout(() => {
      if (this._pointerActivatedToolbarControl === control) this._pointerActivatedToolbarControl = null;
    }, 750);

    if (control.dataset.action === 'clear-format') this._clearFormatting();
    else this._applyToolbarControl(control);
  };

  private _handleColorPickerMouseDown = (event: PointerEvent): void => {
    const path = event.composedPath();
    const picker = path.find((node) => node instanceof QdsColorPicker);
    const trigger = path.find((node) => node instanceof HTMLElement && node.dataset.action === 'open-modal');
    if (!(picker instanceof QdsColorPicker) || picker.dataset.role !== 'text-color' || !trigger) return;

    // Preserve the editor range without focusing the nested trigger and
    // collapsing the range Quill will format when the picker applies a color.
    this._recordShadowSelectionRange();
    event.preventDefault();
    window.setTimeout(() => picker.open(), 0);
  };

  private _handleFontPickerMouseDown = (event: PointerEvent): void => {
    const path = event.composedPath();
    const picker = path.find((node) => node instanceof QdsFontPicker);
    const trigger = path.find((node) => node instanceof HTMLElement && node.dataset.action === 'open-modal');
    if (!(picker instanceof QdsFontPicker) || picker.dataset.role !== 'text-font' || !trigger || !this._quill) return;

    const range = this._recordShadowSelectionRange() || this._quill.getSelection() || this._lastSelectionRange;
    if (range) picker.value = this._getTypographyAtRange(range);
    event.preventDefault();
    window.setTimeout(() => picker.open(), 0);
  };

  private _handleToolbarClick = (event: MouseEvent): void => {
    const control = (event.target as HTMLElement | null)?.closest<HTMLElement>('button[data-format]');
    const action = (event.target as HTMLElement | null)?.closest<HTMLElement>('button[data-action="clear-format"]');
    const activatedControl = control || action;
    if (activatedControl && activatedControl === this._pointerActivatedToolbarControl && event.detail !== 0) {
      this._pointerActivatedToolbarControl = null;
      return;
    }
    if (!control) {
      if (action) this._clearFormatting();
      return;
    }
    this._applyToolbarControl(control);
  };

  private _handleToolbarChange = (event: Event): void => {
    const control = (event.target as HTMLElement | null)?.closest<HTMLSelectElement>('select[data-format]');
    if (!control) return;
    this._applyToolbarControl(control);
  };

  private _handleColorValueChange = (event: CustomEvent<{ value?: unknown }>): void => {
    const picker = event.target as HTMLElement | null;
    const color = event.detail?.value;
    if (!picker?.matches('qds-color-picker[data-role="text-color"]') || typeof color !== 'string') return;
    const range = this._syncSelectionFromShadowRoot() || this._quill?.getSelection() || this._lastSelectionRange;
    if (!range) return;
    this._applyFormat('color', color, range, true, false);
  };

  private _handleFontValueChange = (event: CustomEvent<{ value?: unknown }>): void => {
    const picker = event.target as HTMLElement | null;
    if (!picker?.matches('qds-font-picker[data-role="text-font"]') || !this._quill) return;
    const range = this._syncSelectionFromShadowRoot() || this._quill.getSelection() || this._lastSelectionRange;
    if (!range) return;
    this._applyTypographyStyle(event.detail?.value, range);
  };

  private _getTypographyAtRange(range: QuillSelectionRange): QdsTypographyStyle {
    if (!this._quill) return {};
    const formats = this._quill.getFormat(range);
    const fontSize = typeof formats.size === 'string' ? Number.parseFloat(formats.size) : undefined;
    return {
      ...(typeof formats.font === 'string' && QDS_FONT_FAMILIES.includes(formats.font) ? { fontFamily: formats.font as QdsTypographyStyle['fontFamily'] } : {}),
      ...(Number.isFinite(fontSize) ? { fontSize } : {}),
      ...(typeof formats.color === 'string' ? { color: formats.color } : {}),
      ...(formats.bold ? { bold: true } : {}),
      ...(formats.italic ? { italic: true } : {}),
      ...(formats.underline ? { underline: true } : {}),
      ...(formats.strike ? { strikethrough: true } : {}),
    };
  }

  private _applyTypographyStyle(value: unknown, range: QuillSelectionRange): void {
    const style = value && typeof value === 'object' ? value as QdsTypographyStyle : {};
    this._applyFormat('font', style.fontFamily || false, range, true, false);
    this._applyFormat('size', style.fontSize ? `${style.fontSize}px` : false, range, true, false);
    this._applyFormat('color', style.color || false, range, true, false);
    this._applyFormat('bold', style.bold === true, range, true, false);
    this._applyFormat('italic', style.italic === true, range, true, false);
    this._applyFormat('underline', style.underline === true, range, true, false);
    this._applyFormat('strike', style.strikethrough === true, range, true, false);
  }

  private _clearFormatting(): void {
    if (!this._quill || this.disabled || this.readonly) return;
    const range = this._syncSelectionFromShadowRoot() || this._quill.getSelection() || this._lastSelectionRange;
    if (!range) return;

    if (range.length > 0) {
      this._quill.removeFormat(range.index, range.length, 'user');
    } else {
      const formats = this._quill.getFormat(range);
      Object.entries(formats).forEach(([format, value]) => {
        if (BLOCK_FORMATS.has(format)) {
          this._quill!.formatLine(range.index, 0, format, false, 'user');
        } else if (value) {
          this._quill!.format(format, false, 'user');
        }
      });
    }
    this._quill.setSelection(range.index, range.length, 'silent');
    this._lastSelectionRange = range;
    this._quill.focus({ preventScroll: true });
  }

  private _applyToolbarControl(control: HTMLElement): void {
    if (!this._quill || this.disabled || this.readonly) return;
    const range = this._syncSelectionFromShadowRoot() || this._quill.getSelection() || this._lastSelectionRange;
    if (!range) return;
    const format = control.dataset.format || '';
    if (!format) return;

    if (format === 'link') {
      const url = window.prompt('Enter link URL');
      if (!url) return;
      this._applyFormat('link', url, range);
      return;
    }
    const select = control.tagName === 'SELECT' ? control as HTMLSelectElement : null;
    const rawValue = select ? select.value : control.dataset.value;
    const hasExplicitValue = select !== null || rawValue !== undefined;
    const value = format === 'header' && rawValue ? Number(rawValue) : hasExplicitValue ? rawValue || false : true;
    this._applyFormat(format, value, range, hasExplicitValue, select === null);
  }

  private _applyFormat(format: string, value: unknown, range: QuillSelectionRange, explicitValue = false, toggleSameValue = true): void {
    if (!this._quill) return;
    const currentValue = this._quill.getFormat(range)[format];
    const nextValue = explicitValue && (!toggleSameValue || currentValue !== value) ? value : currentValue ? false : value;
    if (BLOCK_FORMATS.has(format)) {
      this._quill.formatLine(range.index, range.length, format, nextValue, 'user');
    } else if (range.length > 0) {
      this._quill.formatText(range.index, range.length, format, nextValue, 'user');
    } else {
      this._quill.format(format, nextValue, 'user');
    }
    this._quill.setSelection(range.index, range.length, 'silent');
    this._lastSelectionRange = range;
    this._quill.focus({ preventScroll: true });
  }

  /**
   * Quill's editable surface deliberately lives in the component's light DOM.
   * Native browser selections—including iOS selections—can then be represented
   * by document.getSelection() without crossing a Shadow DOM boundary.
   */
  private _getEditorMount(): HTMLElement {
    if (this._editorMount?.isConnected) return this._editorMount;
    const existing = this.querySelector<HTMLElement>(':scope > .qds-rich-text-editor__editor');
    if (existing) {
      this._editorMount = existing;
      return existing;
    }
    const mount = document.createElement('div');
    mount.id = this._id;
    mount.slot = 'editor';
    mount.className = 'qds-rich-text-editor__editor';
    this.appendChild(mount);
    this._editorMount = mount;
    return mount;
  }

  private _resolveShadowSelectionRange(): QuillSelectionRange | null {
    if (!this._quill) return null;
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const nativeRange = selection.getRangeAt(0);
    if (!this._quill.root.contains(nativeRange.startContainer) || !this._quill.root.contains(nativeRange.endContainer)) return null;

    const resolvePoint = (node: Node, offset: number): number | null => {
      if (node.nodeType === Node.TEXT_NODE) {
        const blot = (this._quill!.constructor as typeof Quill).find(node, true) as QuillIndexableBlot | null;
        return blot ? this._quill!.getIndex(blot as any) + blot.index(node, offset) : null;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const child = element.childNodes[offset] || null;
        if (child) return resolvePoint(child, 0);
        const previous = element.childNodes[offset - 1] || null;
        if (previous?.nodeType === Node.TEXT_NODE) return resolvePoint(previous, previous.textContent?.length || 0);
      }
      return null;
    };

    const start = resolvePoint(nativeRange.startContainer, nativeRange.startOffset);
    const end = resolvePoint(nativeRange.endContainer, nativeRange.endOffset);
    return start === null || end === null ? null : { index: Math.min(start, end), length: Math.abs(end - start) };
  }

  private _syncSelectionFromShadowRoot(): QuillSelectionRange | null {
    if (!this._quill) return null;
    // Toolbar pointerdown records the native range before focus can move away
    // from the editable surface. Prefer that stable range during the command.
    const range = this._lastSelectionRange || this._recordShadowSelectionRange();
    if (!range) return null;
    this._quill.setSelection(range.index, range.length, 'silent');
    return range;
  }

  /**
   * Quill reads document.getSelection(), which cannot see this editor's open
   * shadow root. Record the native shadow-root range without writing it back
   * to the DOM; writing a stale range would move the visible caret.
   */
  private _recordShadowSelectionRange(): QuillSelectionRange | null {
    const range = this._resolveShadowSelectionRange();
    if (!range || !this._quill) return null;
    this._lastSelectionRange = range;
    const selection = (this._quill as Quill & { selection?: QuillSelectionState }).selection;
    if (selection) {
      selection.lastRange = range;
      selection.savedRange = range;
    }
    return range;
  }

  private _handleBlur = (): void => {
    if (this.disabled || this.readonly) return;
    this._value = this._quill ? normalizeHtml(this._quill) : this._value;
    this._commitFormValue();
    if (this._validateOn() === 'blur') this._updateValidity(true);
    if (this._value !== this._lastCommittedValue) {
      this._lastCommittedValue = this._value;
      this.dispatchEvent(new CustomEvent('change', { detail: { component: this, value: this._value }, bubbles: true, composed: true }));
    }
  };

  private _validateOn(): QdsValidateOn { return parseValidateOn(this.getAttribute('validate-on')); }

  private _syncValueToEditor(): void {
    if (!this._quill) return;
    if (normalizeHtml(this._quill) === this._value) return;
    this._syncingValue = true;
    if (this._value) this._quill.clipboard.dangerouslyPasteHTML(this._value, 'silent');
    else this._quill.setText('', 'silent');
    this._syncingValue = false;
  }

  private _syncDom(): void {
    if (!this.shadowRoot) return;
    const label = this.shadowRoot.querySelector<HTMLElement>('[part="label"]');
    const labelValue = this.getAttribute('label') || '';
    if (label) {
      label.textContent = labelValue;
      label.style.display = labelValue ? 'block' : 'none';
      if (labelValue && this.required) {
        const marker = document.createElement('span');
        marker.className = 'qds-required-marker'; marker.setAttribute('part', 'required'); marker.setAttribute('aria-hidden', 'true'); marker.textContent = '*'; label.appendChild(marker);
      }
    }
    const hint = this.shadowRoot.querySelector<HTMLElement>('[part="hint"]');
    if (hint) { hint.textContent = this.getAttribute('hint') || ''; hint.style.display = hint.textContent ? 'block' : 'none'; }
    if (this._quill) {
      this._quill.enable(!this.disabled && !this.readonly);
      this._quill.root.dataset.placeholder = this.getAttribute('placeholder') || '';
      this._quill.root.setAttribute('aria-label', labelValue || 'Rich text editor');
      this._quill.root.setAttribute('aria-required', String(this.required));
    }
    this._syncError();
  }

  private _commitFormValue(): void {
    if (!this._internals || typeof this._internals.setFormValue !== 'function') return;
    this._internals.setFormValue(this.disabled ? null : this._value);
  }

  private _updateValidity(showError: boolean): void {
    const missing = this.required && !this._hasTextContent();
    const forced = isAttrTruthy(this.getAttribute('invalid'));
    const custom = Boolean(this._customValidityMessage);
    const message = this._customValidityMessage || (missing ? (this.getAttribute('error-text') || 'Please enter a value.') : forced ? (this.getAttribute('error-text') || 'Invalid value.') : '');
    this._tracker.setValidity({ valid: !missing && !forced && !custom, valueMissing: missing, customError: forced || custom }, message);
    this._errorVisible = showError || forced;
    this._syncError();
    this._maybeDispatchValidityChange();
  }

  private _hasTextContent(): boolean {
    if (this._quill) return Boolean(this._quill.getText().trim());
    const container = document.createElement('div');
    container.innerHTML = this._value;
    return Boolean(container.textContent?.trim());
  }

  private _syncError(): void {
    const error = this.shadowRoot?.querySelector<HTMLElement>('[part="error"]');
    if (!error) return;
    const invalid = !this._tracker.valid;
    const show = invalid && this._errorVisible;
    error.textContent = show ? this._tracker.message : '';
    error.style.display = show ? 'block' : 'none';
    this.toggleAttribute('data-invalid', show);
    this._quill?.root.setAttribute('aria-invalid', String(show));
  }

  private _maybeDispatchValidityChange(): void {
    if (this._tracker.valid !== this._lastValid || this._tracker.message !== this._lastMessage) {
      this._lastValid = this._tracker.valid; this._lastMessage = this._tracker.message;
      dispatchValidityChange(this, this._tracker);
    }
  }

  private render(): void {
    if (!this.shadowRoot) return;
    const toolbar = parseToolbar(this.getAttribute('toolbar'));
    const includesNarrativeTools = toolbar !== 'basic';
    const includesFullTools = toolbar === 'full';
    this.shadowRoot.innerHTML = `
      <style>
        ${getPrimeIconsStyleImport()}
        :host { display: inline-block; width: 100%; min-width: 0; font: inherit; color: var(--theme-text-primary, #333); }
        .qds-rich-text-editor__container { width: 100%; }
        .qds-rich-text-editor__surface { border: 1px solid var(--theme-border-primary, #e0e0e0); border-radius: var(--theme-radius-md, 8px); background: var(--theme-background-primary, #fff); overflow: hidden; transition: border-color .2s ease, box-shadow .2s ease; }
        .qds-rich-text-editor__surface:focus-within { border-color: var(--theme-accent-primary, #4285f4); box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(66,133,244,.2)); }
        :host([readonly]) .qds-rich-text-editor__surface { background: var(--theme-background-secondary, #f8f8f8); }
        :host([disabled]) { opacity: .55; pointer-events: none; }
        :host([invalid]) .qds-rich-text-editor__surface, :host([data-invalid]) .qds-rich-text-editor__surface { border-color: var(--theme-error, #8b1f00); }
        .qds-rich-text-editor__toolbar { display: flex; flex-wrap: wrap; gap: 4px; padding: 6px; border-bottom: 1px solid var(--theme-border-primary, #e0e0e0); background: var(--theme-background-secondary, #f8f8f8); touch-action: manipulation; }
        .qds-rich-text-editor__toolbar button, .qds-rich-text-editor__toolbar select { min-width: 32px; height: 32px; padding: 0 8px; border: 1px solid transparent; border-radius: 4px; background: transparent; color: inherit; font: 600 13px/1 var(--font-family-primary, 'IBM Plex Sans', sans-serif); cursor: pointer; }
        .qds-rich-text-editor__toolbar select { max-width: 116px; border-color: var(--theme-border-primary, #e0e0e0); background: var(--theme-background-primary, #fff); font-weight: 400; }
        .qds-rich-text-editor__toolbar qds-color-picker, .qds-rich-text-editor__toolbar qds-font-picker { display: inline-flex; flex: 0 0 32px; width: 32px; height: 32px; }
        .qds-rich-text-editor__toolbar button:hover { background: var(--button-hover-bg, rgba(0,0,0,.05)); }
        .qds-rich-text-editor__toolbar button:focus-visible { outline: 2px solid var(--theme-accent-primary, #4285f4); outline-offset: 1px; }
        @media (max-width: 1200px) { .qds-rich-text-editor__toolbar button, .qds-rich-text-editor__toolbar select, .qds-rich-text-editor__toolbar qds-color-picker, .qds-rich-text-editor__toolbar qds-font-picker { min-width: 44px; min-height: 44px; height: 44px; } }
        .qds-rich-text-editor__editor { --qds-rich-text-editor-inline-padding: 14px; min-height: 120px; padding: 12px var(--qds-rich-text-editor-inline-padding); }
        /* Quill adds ql-container to this mount element, rather than a child. */
        .qds-rich-text-editor__editor.ql-container { box-sizing: border-box; position: relative; border: 0 !important; outline: 0; box-shadow: none; background: transparent; font: inherit; }
        .qds-rich-text-editor__editor .ql-editor { box-sizing: border-box; min-height: 96px; padding: 0; outline: 0; overflow-y: auto; font: inherit; line-height: 1.6; text-align: left; white-space: pre-wrap; overflow-wrap: break-word; caret-color: currentColor; }
        .qds-rich-text-editor__editor .ql-editor > * { cursor: text; }
        .qds-rich-text-editor__editor .ql-editor.ql-blank::before { content: attr(data-placeholder); position: absolute; left: var(--qds-rich-text-editor-inline-padding); right: var(--qds-rich-text-editor-inline-padding); pointer-events: none; color: var(--qds-rich-text-editor-placeholder-color, #80868b); font-style: normal; }
        .qds-rich-text-editor__editor .ql-editor p, .qds-rich-text-editor__editor .ql-editor ul, .qds-rich-text-editor__editor .ql-editor ol, .qds-rich-text-editor__editor .ql-editor blockquote { margin: 0 0 .8em; }
        /* Quill stores both list types as <ol>; its data-list marker needs
           these core rules to render bullets instead of browser numbering. */
        .qds-rich-text-editor__editor .ql-editor ol { padding-left: 1.5em; counter-reset: qds-list; }
        .qds-rich-text-editor__editor .ql-editor li { position: relative; padding-left: 1.5em; list-style: none; }
        .qds-rich-text-editor__editor .ql-editor li > .ql-ui::before { display: inline-block; width: 1.2em; margin-left: -1.5em; margin-right: .3em; text-align: right; white-space: nowrap; }
        .qds-rich-text-editor__editor .ql-editor li[data-list="bullet"] > .ql-ui::before { content: '•'; }
        .qds-rich-text-editor__editor .ql-editor li[data-list="ordered"] { counter-increment: qds-list; }
        .qds-rich-text-editor__editor .ql-editor li[data-list="ordered"] > .ql-ui::before { content: counter(qds-list, decimal) '. '; }
        .qds-rich-text-editor__editor .ql-editor p:last-child, .qds-rich-text-editor__editor .ql-editor ul:last-child, .qds-rich-text-editor__editor .ql-editor ol:last-child, .qds-rich-text-editor__editor .ql-editor blockquote:last-child { margin-bottom: 0; }
        .qds-rich-text-editor__editor .ql-editor blockquote { margin-left: 0; padding-left: 12px; border-left: 3px solid var(--theme-border-strong, #c5c9ce); color: var(--theme-text-secondary, #5f6368); }
        .qds-rich-text-editor__editor .ql-editor h1, .qds-rich-text-editor__editor .ql-editor h2, .qds-rich-text-editor__editor .ql-editor h3 { margin: 0 0 .5em; line-height: 1.25; }
        .qds-rich-text-editor__editor .ql-editor h1 { font-size: 1.7em; } .qds-rich-text-editor__editor .ql-editor h2 { font-size: 1.4em; } .qds-rich-text-editor__editor .ql-editor h3 { font-size: 1.15em; }
        .qds-rich-text-editor__editor .ql-editor .ql-code-block-container { margin: 0 0 .8em; padding: 12px; border-radius: 4px; background: var(--theme-background-secondary, #f8f8f8); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .qds-hint, .qds-error { font-size: 13px; line-height: 1.4; margin-top: 4px; }
        .qds-hint { color: var(--theme-text-tertiary, #6a6a6a); } .qds-error { color: var(--theme-error, #8b1f00); }
      </style>
      <div class="qds-rich-text-editor__container" part="container">
        <label class="qds-label" part="label" for="${this._id}" style="display:none"></label>
        <div class="qds-rich-text-editor__surface" part="wrapper">
          <div class="qds-rich-text-editor__toolbar" part="toolbar" aria-label="Text formatting">
            <button type="button" class="ql-bold" data-format="bold" aria-label="Bold"><strong>B</strong></button>
            <button type="button" class="ql-italic" data-format="italic" aria-label="Italic"><em>I</em></button>
            <button type="button" class="ql-underline" data-format="underline" aria-label="Underline"><u>U</u></button>
            ${includesFullTools ? '<button type="button" class="ql-strike" data-format="strike" aria-label="Strikethrough"><s>S</s></button>' : ''}
            ${includesNarrativeTools ? '<button type="button" class="ql-link" data-format="link" aria-label="Link">Link</button><button type="button" class="ql-list" data-format="list" data-value="bullet" aria-label="Bulleted list">• List</button><button type="button" class="ql-list" data-format="list" data-value="ordered" aria-label="Numbered list">1. List</button><button type="button" class="ql-blockquote" data-format="blockquote" aria-label="Quote">Quote</button>' : ''}
            ${includesFullTools ? '<select data-format="header" aria-label="Heading"><option value="">Paragraph</option><option value="1">Heading 1</option><option value="2">Heading 2</option><option value="3">Heading 3</option></select><qds-font-picker toolbar data-role="text-font"></qds-font-picker><qds-color-picker toolbar data-role="text-color" value="var(--theme-text-primary, #333333)"></qds-color-picker><button type="button" data-format="align" data-value="" aria-label="Align left" title="Align left"><i data-lucide="align-left" aria-hidden="true"></i></button><button type="button" data-format="align" data-value="center" aria-label="Align center" title="Align center"><i data-lucide="align-center" aria-hidden="true"></i></button><button type="button" data-format="align" data-value="right" aria-label="Align right" title="Align right"><i data-lucide="align-right" aria-hidden="true"></i></button><button type="button" data-format="align" data-value="justify" aria-label="Justify" title="Justify"><i data-lucide="align-justify" aria-hidden="true"></i></button><button type="button" data-format="indent" data-value="-1" aria-label="Decrease indent" title="Decrease indent"><i data-lucide="outdent" aria-hidden="true"></i></button><button type="button" data-format="indent" data-value="+1" aria-label="Increase indent" title="Increase indent"><i data-lucide="indent" aria-hidden="true"></i></button><button type="button" data-format="script" data-value="super" aria-label="Superscript">X²</button><button type="button" data-format="script" data-value="sub" aria-label="Subscript">X₂</button><button type="button" data-format="code-block" aria-label="Code block">Code</button><button type="button" data-action="clear-format" aria-label="Clear formatting" title="Clear formatting"><i class="pi pi-eraser" aria-hidden="true"></i></button>' : ''}
          </div>
          <div class="qds-rich-text-editor__editor-slot" part="editor"><slot name="editor"></slot></div>
        </div>
        <div class="qds-hint" part="hint" style="display:none"></div>
        <div class="qds-error" part="error" role="status" aria-live="polite" style="display:none"></div>
      </div>`;
    hydrateQdsLucideIcons(this.shadowRoot);
  }
}

if (!customElements.get('qds-rich-text-editor')) customElements.define('qds-rich-text-editor', QdsRichTextEditor);

export default QdsRichTextEditor;
